/**
 * Ladder generator — a straight or A-frame (stepladder) ladder.
 *
 * Coordinate convention: Z-up, 1 unit = 1 metre.
 *   - X = width (rung span)
 *   - Y = depth (A-frame spread, zero for straight style)
 *   - Z = height
 *
 * Local anchor: origin at the geometric centre of the footprint at Z = 0.
 * For the straight style the two rails are symmetric about the Y axis, and
 * the bottom rail ends sit flush with Z = 0. The user can tilt the ladder
 * object in the viewport for a leaning-against-wall pose.
 *
 * Styles:
 *   straight   — a simple extension-ladder style: two vertical rails with
 *                evenly-spaced rungs. The canonical leaning-ladder shape.
 *   stepladder — an A-frame self-standing step ladder. Two mirrored straight
 *                sections meeting at the top, spread apart at the base by
 *                `spreadAngle`. A folding spreader bar connects the two
 *                halves at mid-height.
 *
 * Rail cross-section is rectangular (`railWidth × railDepth`). Rungs are
 * thinner rectangular bars spanning between the inner rail faces.
 *
 * The bottom rail ends have small rubber-foot stubs for the straight style,
 * and four feet (one per rail bottom) for the stepladder.
 */

import type { PrimitiveGeometry } from "../primitives.ts";
import type { BoxSpec } from "./generatorGeom.ts";
import {
  buildBox,
  concatBoxes,
  concatPrimitiveGeometries,
  transformGeometry,
} from "./generatorGeom.ts";
import type {
  Generator,
  GeneratorBuildContext,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import {
  boolParam,
  defineGenerator,
  enumParam,
  intParam,
  numParam,
  paramsFromSpecs,
} from "./types.ts";

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

interface LadderParams extends GeneratorParams {
  style: string;
  rungCount: number;
  rungSpacing: number;
  width: number;
  railWidth: number;
  railDepth: number;
  rungThickness: number;
  spreadAngle: number;
  hasFeet: boolean;
}

const LADDER_SPECS: readonly GeneratorParamSpec[] = [
  {
    type: "section",
    id: "ladder-structure",
    label: "Structure",
    params: [
      {
        id: "style",
        label: "Style",
        type: "enum",
        default: "straight",
        options: [
          { value: "straight", label: "Straight (Extension)" },
          { value: "stepladder", label: "A-Frame (Stepladder)" },
        ],
      },
      {
        id: "rungCount",
        label: "Rungs",
        type: "number",
        unit: "count",
        default: 8,
        min: 2,
        max: 24,
        step: 1,
      },
      {
        id: "rungSpacing",
        label: "Rung Spacing",
        type: "number",
        unit: "meters",
        default: 0.28,
        min: 0.2,
        max: 0.4,
        step: 0.01,
      },
      {
        id: "width",
        label: "Width",
        type: "number",
        unit: "meters",
        default: 0.46,
        min: 0.3,
        max: 0.9,
        step: 0.01,
      },
      {
        id: "spreadAngle",
        label: "Spread Angle",
        type: "number",
        unit: "degrees",
        default: 30,
        min: 15,
        max: 60,
        step: 1,
        showIf: { id: "style", value: "stepladder" },
      },
    ],
  },
  {
    type: "section",
    id: "ladder-dimensions",
    label: "Dimensions",
    defaultCollapsed: true,
    params: [
      {
        id: "railWidth",
        label: "Rail Width",
        type: "number",
        unit: "meters",
        default: 0.04,
        min: 0.02,
        max: 0.08,
        step: 0.005,
      },
      {
        id: "railDepth",
        label: "Rail Depth",
        type: "number",
        unit: "meters",
        default: 0.025,
        min: 0.01,
        max: 0.06,
        step: 0.005,
      },
      {
        id: "rungThickness",
        label: "Rung Thickness",
        type: "number",
        unit: "meters",
        default: 0.025,
        min: 0.01,
        max: 0.05,
        step: 0.005,
      },
      { id: "hasFeet", label: "Rubber Feet", type: "boolean", default: true },
    ],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Build one straight ladder section (rails + rungs) in its own local space:
 *   - Rails run along Z from 0 to totalHeight.
 *   - Rungs span between the inner faces of the rails.
 *   - The section is centred in X (rails at ±(width/2 - railWidth/2)).
 *   - Section depth (Y) = railDepth, centred at Y = 0.
 */
function buildSection(
  rungCount: number,
  rungSpacing: number,
  width: number,
  railWidth: number,
  railDepth: number,
  rungThickness: number,
  hasFeet: boolean,
): PrimitiveGeometry {
  const totalH = rungCount * rungSpacing;
  const railX = width * 0.5 - railWidth * 0.5;
  const boxes: BoxSpec[] = [];

  // Two rails
  boxes.push({
    cx: railX,
    cy: 0,
    cz: totalH * 0.5,
    sx: railWidth,
    sy: railDepth,
    sz: totalH,
  });
  boxes.push({
    cx: -railX,
    cy: 0,
    cz: totalH * 0.5,
    sx: railWidth,
    sy: railDepth,
    sz: totalH,
  });

  // Rungs — span between inner rail faces, centred between the rails
  const rungSpan = Math.max(0.01, width - railWidth * 2);
  for (let i = 0; i < rungCount; i++) {
    // First rung sits at rungSpacing/2 from bottom; last near the top
    const rz = rungSpacing * 0.5 + i * rungSpacing;
    boxes.push({
      cx: 0,
      cy: 0,
      cz: rz,
      sx: rungSpan,
      sy: rungThickness,
      sz: rungThickness,
    });
  }

  // Rubber feet — small square stubs at the bottom of each rail
  if (hasFeet) {
    const footH = 0.015;
    const footW = railWidth * 1.2;
    boxes.push({
      cx: railX,
      cy: 0,
      cz: -footH * 0.5,
      sx: footW,
      sy: railDepth * 1.2,
      sz: footH,
    });
    boxes.push({
      cx: -railX,
      cy: 0,
      cz: -footH * 0.5,
      sx: footW,
      sy: railDepth * 1.2,
      sz: footH,
    });
  }

  return concatBoxes(boxes);
}

// ---------------------------------------------------------------------------
// Build
// ---------------------------------------------------------------------------

function buildLadder(
  p: LadderParams,
  _ctx: GeneratorBuildContext,
): GeneratorOutput {
  const style = enumParam(p, "style", "straight");
  const rungCount = intParam(p, "rungCount", 8);
  const rungSpacing = numParam(p, "rungSpacing", 0.28);
  const width = numParam(p, "width", 0.46);
  const railW = numParam(p, "railWidth", 0.04);
  const railD = numParam(p, "railDepth", 0.025);
  const rungT = numParam(p, "rungThickness", 0.025);
  const spreadDeg = numParam(p, "spreadAngle", 30);
  const hasFeet = boolParam(p, "hasFeet", true);

  const totalH = rungCount * rungSpacing;

  if (style === "straight") {
    const geo = buildSection(
      rungCount,
      rungSpacing,
      width,
      railW,
      railD,
      rungT,
      hasFeet,
    );
    return { kind: "mesh", geometry: geo };
  }

  // ── A-frame (stepladder) ──────────────────────────────────────────────────
  // Two mirrored straight sections, each tilted inward by spreadAngle/2 around
  // the X axis, meeting at the top. We rotate each half about its top edge so
  // the apex stays at (0, 0, totalH) and the feet spread apart at Z = 0.
  //
  // The tilt angle for each half relative to vertical = spreadAngle / 2.
  // Each section is built vertically (Z-up), then rotated about its top.
  //
  // After rotating half-section by angle θ outward:
  //   foot Y offset = totalH × sin(θ)
  //   actual height = totalH × cos(θ)  (so apex is at cos(θ) * totalH)
  //
  // We shift both halves down by (totalH - totalH×cos(θ)) = totalH(1-cos(θ))
  // so the feet land exactly at Z = 0 and the apex is at Z = totalH×cos(θ).

  const halfAngle = spreadDeg * 0.5 * (Math.PI / 180);
  const cosA = Math.cos(halfAngle);
  const sinA = Math.sin(halfAngle);

  const sectionGeo = buildSection(
    rungCount,
    rungSpacing,
    width,
    railW,
    railD,
    rungT,
    hasFeet,
  );

  // Each section: rotate top edge inward.
  // Front half (+Y spread): tilt by +halfAngle around X (top moves toward −Y, foot toward +Y).
  // Rear half  (−Y spread): tilt by −halfAngle around X.
  //
  // Pivot is the section top (Z = totalH). Steps per side:
  //   1. Translate top to origin:  shift by [0, 0, −totalH]
  //   2. Rotate Rx(±halfAngle):    foot moves to (0, ∓totalH·sinA, −totalH·cosA)
  //   3. Translate so feet land at Z = 0, apex at Z = totalH·cosA: shift [0, 0, +totalH·cosA]
  //
  // Both apexes end up at (0, 0, totalH·cosA) — they meet at the top. ✓
  const makeSide = (sign: 1 | -1): PrimitiveGeometry => {
    const centred = transformGeometry(sectionGeo, [0, 0, -totalH]);
    const rotated = transformGeometry(
      centred,
      [0, 0, 0],
      [sign * halfAngle, 0, 0],
    );
    return transformGeometry(rotated, [0, 0, totalH * cosA]);
  };

  const front = makeSide(1);
  const rear = makeSide(-1);

  // Spreader bar: horizontal brace at half the ladder height.
  // At Z = totalH·cosA·0.5, each section's rail sits at Y = ±totalH·sinA·0.5
  // (linear interpolation between apex Y=0 and foot Y=±totalH·sinA).
  const spreaderZ = totalH * cosA * 0.5;
  const spreaderY = totalH * sinA * 0.5;
  const spreaderLen = Math.max(0.01, 2 * spreaderY);
  const spreaderGeo = transformGeometry(
    buildBox(width * 0.4, spreaderLen, railD),
    [0, 0, spreaderZ],
  );

  return {
    kind: "mesh",
    geometry: concatPrimitiveGeometries([front, rear, spreaderGeo]),
  };
}

// ---------------------------------------------------------------------------
// Generator definition
// ---------------------------------------------------------------------------

export const LADDER_GENERATOR: Generator = defineGenerator<LadderParams>({
  id: "ladder",
  label: "Ladder",
  category: "prop",
  paramSpecs: LADDER_SPECS,
  defaultParams: paramsFromSpecs(LADDER_SPECS) as LadderParams,
  build: buildLadder,
});
